import { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';
import { SearchableSelect } from '../../../components/ui/searchable-select';
import { Users, Printer, ChevronDown, X, Mail, Phone, Check } from 'lucide-react';

interface GroupOption {
  id: number;
  groupName: string;
  groupCode: string;
  periodStatus: string;
  startDate: string;
  endDate: string;
  programId: number;
  programName: string;
  studentCount: number;
  maxStudents: number | null;
}

interface Student {
  id: number;
  nui: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  email: string;
  telefono: string;
  estatus: string;
  hasPaid: boolean;
}

interface GroupWithStudents {
  groupId: number;
  groupName: string;
  groupCode: string;
  periodStatus: string;
  programName: string;
  students: Student[];
  maxStudents?: number | null;
}

export default function ListasGrupos() {
  const { toast } = useToast();
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [groupsData, setGroupsData] = useState<GroupWithStudents[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await fetch('/api/attendance/groups', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      } else {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los grupos',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast({
        title: 'Error',
        description: 'Error de conexion al cargar grupos',
        variant: 'destructive',
      });
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroup = (groupId: number) => {
    const newSelected = new Set(selectedGroupIds);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroupIds(newSelected);
  };

  const removeGroup = (groupId: number) => {
    const newSelected = new Set(selectedGroupIds);
    newSelected.delete(groupId);
    setSelectedGroupIds(newSelected);
  };

  const selectAll = () => {
    if (selectedGroupIds.size === filteredGroups.length) {
      setSelectedGroupIds(new Set());
    } else {
      setSelectedGroupIds(new Set(filteredGroups.map(g => g.id)));
    }
  };

  const generateReport = async () => {
    if (selectedGroupIds.size === 0) {
      toast({
        title: 'Aviso',
        description: 'Selecciona al menos un grupo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/attendance/groups/students', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ groupIds: Array.from(selectedGroupIds) }),
      });

      if (response.ok) {
        const data: GroupWithStudents[] = await response.json();
        const groupMap = new Map(groups.map(g => [g.id, g]));
        const enriched = data.map(gd => ({
          ...gd,
          maxStudents: groupMap.get(gd.groupId)?.maxStudents ?? null,
        }));
        setGroupsData(enriched);
        setDropdownOpen(false);
      } else {
        toast({
          title: 'Error',
          description: 'No se pudo generar el reporte',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Error',
        description: 'Error de conexion al generar reporte',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const PERIOD_STATUS_LABELS: Record<string, string> = {
    draft: 'Borrador',
    active: 'Activo',
    closed: 'Cerrado',
    promocion: 'Promoción',
    graduado: 'Graduado',
  };

  const PERIOD_STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-200 text-gray-800',
    active: 'bg-green-200 text-green-800',
    closed: 'bg-red-200 text-red-800',
    promocion: 'bg-indigo-200 text-indigo-800',
    graduado: 'bg-teal-200 text-teal-800',
  };

  const getStatusBadge = (status: string) => {
    const colors: { [key: string]: string } = {
      activo: 'bg-green-100 text-green-800',
      inscrito: 'bg-blue-100 text-blue-800',
      aspirante: 'bg-yellow-100 text-yellow-800',
      egresado: 'bg-purple-100 text-purple-800',
      baja: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredGroups = groups.filter(group => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      group.groupName?.toLowerCase().includes(search) ||
      group.programName?.toLowerCase().includes(search) ||
      group.groupCode?.toLowerCase().includes(search);
    const matchesStatus = !statusFilter || group.periodStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Build status options from actual data (only show statuses that exist)
  const statusOptions = Array.from(
    new Map(
      groups
        .filter(g => g.periodStatus)
        .map(g => [g.periodStatus, { value: g.periodStatus, label: PERIOD_STATUS_LABELS[g.periodStatus] || g.periodStatus }])
    ).values()
  ).sort((a, b) => a.label.localeCompare(b.label));

  const selectedGroups = groups.filter(g => selectedGroupIds.has(g.id));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listas de Grupos</h1>
          <p className="text-gray-600">Consulta los alumnos inscritos por grupo</p>
        </div>
        {groupsData.length > 0 && (
          <Button onClick={handlePrint} variant="outline" className="print:hidden">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
        )}
      </div>

      {/* Group Selection - Compact Multi-Select */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6 print:hidden">

        {/* Filter row: status + count */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-4">
          <div className="w-full sm:w-64">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Filtrar por estatus del período
            </label>
            <SearchableSelect
              options={statusOptions}
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val ? String(val) : null);
                // Clear selections that no longer match the new filter
                if (val) {
                  const validIds = new Set(
                    groups
                      .filter(g => g.periodStatus === String(val))
                      .map(g => g.id)
                  );
                  setSelectedGroupIds(prev => new Set([...prev].filter(id => validIds.has(id))));
                }
              }}
              placeholder="Todos los estatus"
            />
          </div>
          {statusFilter && (
            <button
              className="text-xs text-blue-600 hover:text-blue-800 underline mt-4 sm:mt-5 whitespace-nowrap"
              onClick={() => setStatusFilter(null)}
            >
              Quitar filtro
            </button>
          )}
          <div className="sm:ml-auto text-sm text-gray-500 mt-1 sm:mt-5">
            {filteredGroups.length} grupo{filteredGroups.length !== 1 ? 's' : ''} disponible{filteredGroups.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          {/* Multi-Select Dropdown */}
          <div className="flex-1 w-full" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Grupos
            </label>
            <div className="relative">
              <div
                onClick={() => !loadingGroups && setDropdownOpen(!dropdownOpen)}
                className={`min-h-[42px] px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between ${
                  loadingGroups ? 'bg-gray-100' : 'bg-white hover:border-blue-400'
                }`}
              >
                <div className="flex flex-wrap gap-1 flex-1">
                  {loadingGroups ? (
                    <span className="text-gray-400">Cargando grupos...</span>
                  ) : selectedGroups.length === 0 ? (
                    <span className="text-gray-400">Selecciona uno o mas grupos...</span>
                  ) : selectedGroups.length <= 3 ? (
                    selectedGroups.map(group => (
                      <span
                        key={group.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-sm rounded"
                      >
                        <span className="font-medium">{group.groupCode}</span> - {group.groupName}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeGroup(group.id);
                          }}
                        />
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-700">{selectedGroups.length} grupos seleccionados</span>
                  )}
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </div>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-80 overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Buscar grupo o programa..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {/* Select All */}
                  <div
                    className="px-3 py-2 border-b bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                    onClick={selectAll}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedGroupIds.size === filteredGroups.length && filteredGroups.length > 0
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedGroupIds.size === filteredGroups.length && filteredGroups.length > 0 && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      {selectedGroupIds.size === filteredGroups.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </span>
                  </div>

                  {/* Options List */}
                  <div className="overflow-y-auto max-h-56">
                    {filteredGroups.length === 0 ? (
                      <div className="px-3 py-4 text-center text-gray-500 text-sm">
                        No se encontraron grupos
                      </div>
                    ) : (
                      filteredGroups.map((group) => (
                        <div
                          key={group.id}
                          className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-50 ${
                            selectedGroupIds.has(group.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => toggleGroup(group.id)}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            selectedGroupIds.has(group.id)
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {selectedGroupIds.has(group.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                              <span className="text-blue-600">{group.groupCode}</span>
                              {group.periodStatus && (
                                <span className={`inline-flex px-1.5 py-0 text-[10px] font-semibold rounded-full ${PERIOD_STATUS_COLORS[group.periodStatus] || 'bg-gray-200 text-gray-800'}`}>
                                  {PERIOD_STATUS_LABELS[group.periodStatus] || group.periodStatus}
                                </span>
                              )}
                              <span className="truncate">- {group.groupName}</span>
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {group.programName} - {group.studentCount} alumnos
                              {group.periodStatus === 'promocion' && group.maxStudents != null && (
                                <span className={`ml-1.5 font-medium ${
                                  group.maxStudents - group.studentCount > 0
                                    ? 'text-indigo-600'
                                    : 'text-red-600'
                                }`}>
                                  · {Math.max(0, group.maxStudents - group.studentCount)} lugar{Math.max(0, group.maxStudents - group.studentCount) !== 1 ? 'es' : ''} disponible{Math.max(0, group.maxStudents - group.studentCount) !== 1 ? 's' : ''}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <Button 
            onClick={generateReport} 
            disabled={loading || selectedGroupIds.size === 0}
            className="whitespace-nowrap"
          >
            {loading ? 'Generando...' : 'Generar Reporte'}
          </Button>
        </div>

        {/* Selected Count */}
        {selectedGroupIds.size > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {selectedGroupIds.size} grupo{selectedGroupIds.size > 1 ? 's' : ''} seleccionado{selectedGroupIds.size > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Report Display */}
      {groupsData.length > 0 && (
        <div className="space-y-6">
          {groupsData.map((groupData) => {
            const qualifiedStudents = groupData.students.filter(
              s => s.hasPaid || s.estatus === 'aceptado'
            );
            const qualifiedCount = qualifiedStudents.length;
            const maxCupo = groupData.maxStudents ?? null;
            const lugaresDisponibles = maxCupo != null ? Math.max(0, maxCupo - qualifiedCount) : null;
            const isPromocion = groupData.periodStatus === 'promocion';

            return (
            <div key={groupData.groupId} className="bg-white rounded-lg shadow-sm border overflow-hidden print:shadow-none print:border-gray-300">
              <div className="bg-blue-600 text-white px-6 py-4 print:bg-gray-100 print:text-gray-900">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold print:text-lg flex items-center gap-2 flex-wrap">
                      {groupData.programName}
                    </h2>
                    <p className="text-blue-100 print:text-gray-600 flex items-center gap-2 flex-wrap">
                      {groupData.groupCode && <span className="font-semibold">{groupData.groupCode}</span>}
                      {groupData.groupCode && <span>-</span>}
                      {groupData.groupName}
                      {groupData.periodStatus && (
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${PERIOD_STATUS_COLORS[groupData.periodStatus] || 'bg-gray-200 text-gray-800'}`}>
                          {PERIOD_STATUS_LABELS[groupData.periodStatus] || groupData.periodStatus}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    {maxCupo != null ? (
                      <>
                        <p className="text-2xl font-bold print:text-xl leading-tight">
                          {qualifiedCount}
                          <span className="text-base font-normal text-blue-200 print:text-gray-500"> de {maxCupo}</span>
                        </p>
                        <p className="text-blue-100 text-sm print:text-gray-600">
                          Alumnos inscritos
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold print:text-xl">{qualifiedCount}</p>
                        <p className="text-blue-100 text-sm print:text-gray-600">Alumnos inscritos</p>
                      </>
                    )}
                    {isPromocion && maxCupo != null && (
                      <p className={`text-sm font-semibold mt-1 ${
                        lugaresDisponibles! > 0
                          ? 'text-yellow-200 print:text-indigo-700'
                          : 'text-red-300 print:text-red-600'
                      }`}>
                        {lugaresDisponibles} lugar{lugaresDisponibles !== 1 ? 'es' : ''} disponible{lugaresDisponibles !== 1 ? 's' : ''}
                      </p>
                    )}
                    {isPromocion && maxCupo == null && (
                      <p className="text-xs text-yellow-200 print:text-amber-600 mt-1">Sin cupo máximo definido</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NUI</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre Completo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden">Contacto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groupData.students.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No hay alumnos inscritos en este grupo
                        </td>
                      </tr>
                    ) : (
                      groupData.students.map((student, index) => (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.nui}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {student.apellidoPaterno} {student.apellidoMaterno}, {student.nombre}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 print:hidden">
                            <div className="space-y-1">
                              {student.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-[200px]">{student.email}</span>
                                </div>
                              )}
                              {student.telefono && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span>{student.telefono}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(student.estatus)}`}>
                              {student.estatus}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && groupsData.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center print:hidden">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Selecciona uno o varios grupos y presiona "Generar Reporte" para ver las listas de alumnos</p>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .space-y-6, .space-y-6 * {
            visibility: visible;
          }
          .space-y-6 {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
